import styled from '@emotion/styled'
import { Dropdown, Dropup } from 'app/components/Icons'
import { AnimatePresence, motion } from 'framer-motion'
import React, { memo } from 'react'
import {
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemState,
} from 'react-accessible-accordion'

export interface Props {
  description?: string
  isExpanded?: boolean
  title?: string
}

export const Item = memo(function Item({ description, title }: Props) {
  if (!title) {
    return null
  }

  return (
    <Container activeClassName="expanded">
      <Head>
        <Title>
          {title}
          <Button>
            <State>
              {({ expanded }) => (expanded ? <Dropup /> : <Dropdown />)}
            </State>
          </Button>
        </Title>
      </Head>
      {description ? (
        <Panel>
          <State>
            {({ expanded }) => (
              <AnimatePresence>
                {expanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{
                      opacity: 1,
                      height: 'auto',
                    }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Description
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </State>
        </Panel>
      ) : null}
    </Container>
  )
})

const Container = styled(AccordionItem)`
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  margin-top: 0.25rem;
`

const Head = styled(AccordionItemHeading)`
  cursor: pointer;
`

const Title = styled(AccordionItemButton)`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1.0625rem;
  font-weight: 500;
  line-height: 1.25rem;
  padding: 2rem 2.1875rem 1.75rem 4.1875rem;
  position: relative;
  text-transform: uppercase;
  &:before {
    content: '';
    width: 1.875rem;
    height: 0.125rem;
    background: ${({ theme }) => theme.colors.variants.neutralDark2};
    position: absolute;
    top: 2.5625rem;
    left: 1.25rem;
  }

  @media (max-width: 1199px) {
    font-size: 1rem;
    padding-top: 1.75rem;
    padding-bottom: 1.5rem;
    &:before {
      content: '';
      top: 2.3125rem;
    }
  }

  @media (max-width: 767px) {
    padding-right: 1.25rem;
    padding-left: 3.125rem;
    &:before {
      width: 1.25rem;
    }
  }
`

const Button = styled.div`
  margin-left: auto;

  svg {
    width: auto;
    height: 1rem;
    fill: none;
    stroke: ${({ theme }) => theme.colors.variants.neutralDark2};
    stroke-width: 1.3;
  }
`

const State = styled(AccordionItemState)``

const Panel = styled(AccordionItemPanel)`
  display: block !important;
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.9375rem;
  line-height: 1.5rem;
  overflow: hidden;
  padding: 0 3rem 0 4.1875rem;

  @media (max-width: 767px) {
    padding: 0 1.25rem;
  }
`

const Description = styled.div`
  padding: 1.25rem 0 3rem;

  p {
    margin-block-end: 1em;
  }

  ul {
    margin: 1em 0;
    padding: inherit;
    li {
      padding-left: 0.875rem;
      position: relative;
      &:before {
        content: '';
        width: 0.3125rem;
        height: 0.3125rem;
        background: ${({ theme }) => theme.colors.variants.neutralDark4};
        border-radius: 50%;
        position: absolute;
        top: 0.8125rem;
        left: 0;
      }
    }
  }

  @media (max-width: 767px) {
    padding: 1rem 0 1.875rem;
  }
`
